/**
 * 작성자 : 박승호
 * 날짜 : 2024.08.16
 * 경로 : 본사 현장 CCTV - CCTV 현황
 */

import { Dispatch, SetStateAction, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { LIST_COUNT, PMS_NORMAL_SUB_CODE } from '../../_constants';
import { ContentsContainerRoot } from '../../assets/styles/ContentsContainerRoot';
import { PulseLoader } from 'react-spinners';
import { LoadingModalBackground } from '../../assets/styles/Modal';
import { SearchOptions } from '../../assets/styles/SearchOptions';
import Tab from '../../components/Tab';
import { useLocation, useNavigate, useOutletContext } from 'react-router-dom';
import { IUser, pageInfoState, userState } from '../../atoms';
import { useRecoilValue } from 'recoil';
import { logPost } from '../../services/log';
import { BtnGhost } from '../../components/Button';
import { useSetAuth } from '../../utils/useSetAuth';
import { IComCdList, ITabObject, ISelectBox } from 'customTypes';
import { useDetectScrolledToBottom } from '../../utils/useDetectScrolledToBottom';
import { FloatingButtonToTop } from '../../assets/styles/FloatingButtonToTop';
import { IoChevronUpSharp } from 'react-icons/io5';
import { scrollToNodeTop } from '../../utils/scrollToNodeTop';
import ShortcutButton from '../../components/button/ShortcutButton';
import TuiGridWrapper from '../../components/Table/TuiGridWrapper';
import i18n from '../../translation/i18n';
import TuiGrid from '../../components/Table/TuiGrid';
import SearchSelectBoxSm from '../../components/SearchSelectBoxSm';
import { apiGet } from '../../services/_common';
import { useTranslation } from 'react-i18next';
import { setComCdListState } from '../../utils/setComCdListState';
import useSetListCount from '../../utils/useSetListCount';
import SelectBox from '../../components/SelectBox';
import { useGetFrozenCount } from '../../utils/getFrozenCount';
import useOnKeydownF9 from '../../utils/useOnKeydownF9';
import Input from '../../components/Input';
import useTuiGridSize from '../../hooks/useTuiGridSize';

const Root = styled(ContentsContainerRoot)`
  display: flex;
  flex-direction: column;
  background-color: white;
  border-radius: 0.5rem;
  padding: 2rem 0;

  > :nth-child(1) {
    align-self: flex-start;
  }
`;

const Hcctv2 = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const size = useOutletContext<any>();
  const { auth } = useSetAuth();
  const userInfo = useRecoilValue<IUser>(userState);
  const { isF9Pressed, setIsF9Pressed } = useOnKeydownF9(); // f9키 프레스 훅
  const { userMenuList, eExcelYn, eListYn } = userInfo;
  const tabInfo = useRecoilValue(pageInfoState);
  const location = useLocation();
  const frozenCount = useGetFrozenCount();
  const { listCount, setListCount, patchUserMenuAPI } = useSetListCount(); // 검색줄수설정 훅
  const tableRef = useRef<HTMLDivElement>(null);
  const { isBottom } = useDetectScrolledToBottom(tableRef);
  const [listCountComCdList, setListCountComCdList] = useState<IComCdList[]>([]); // 검색줄수 공통코드
  const [constructionList, setConstructionList] = useState<ISelectBox[]>([]); // 건설업종 메뉴 리스트
  const [orgCamCList, setOrgCamCList] = useState<ISelectBox[]>([]); // 관리회사명 메뉴 전체 리스트
  const [camCList, setCamCList] = useState<ISelectBox[]>([]); // 관리회사명 메뉴 리스트
  const [nvrList, setNvrList] = useState<ISelectBox[]>([]); // 현장명 메뉴 리스트
  const [construction, setConstruction] = useState<ISelectBox>({ type: '', cdName: '' });
  const [camCcd, setCamCcd] = useState<ISelectBox>({ type: '', cdName: '' });
  const [nvrName, setNvrName] = useState<any>({ nvrName: '' });
  const [columns, setColumns] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [currentTabMrCd, setCurrentTabMrCd] = useState(tabInfo.defaultMrCd);
  const [newTabList, setNewTabList] = useState<ITabObject[]>();
  const [tableState, setTableState] = useState<any[]>([]);
  const [excelBtn, setExcelBtn] = useState(false);
  const [filterTableState, setFilterTableState] = useState<any[]>([]);
  const componentRef = useRef<HTMLDivElement>(null);
  const { tuiHeight } = useTuiGridSize(componentRef);

  const subCodeFilter = (grCd: string) => {
    const codeFilter = PMS_NORMAL_SUB_CODE.filter((el) => el.code.includes(grCd));
    return codeFilter[0].type;
  };

  useEffect(() => {
    const arr: any[] = [];
    userMenuList.map((v: any) => {
      const result = tabInfo?.tabList?.find((el: ITabObject) => el.mtCd === v.mtCd && el.mdCd === v.mdCd && el.mrCd === v.mrCd);
      if (result) {
        arr.push(result);
        setNewTabList(arr);
      }
    });
  }, [tabInfo.tabList]);

  useEffect(() => {
    setColumns([
      {
        header: t('건설업종'),
        name: 'infraName',
        align: 'left',
        sortable: true,
        width: 180,
        renderer: { classNames: ['text_secondary'] },
      },
      {
        header: t('관리회사명'),
        name: 'camCName',
        align: 'left',
        sortable: true,
        Width: 120,
      },
      {
        header: t('NVR명'),
        name: 'nName',
        align: 'left',
        sortable: true,
        width: 120,
        renderer: { classNames: ['text_secondary'] },
      },
      {
        header: t('착공일'),
        name: 'startDate',
        align: 'center',
        sortable: true,
        minWidth: 120,
      },
      {
        header: t('준공 예정일'),
        name: 'endDate',
        align: 'center',
        sortable: true,
        minWidth: 120,
      },
      {
        header: t('담당자명 / 연락처'),
        name: 'nh',
        align: 'center',
        sortable: true,
        minWidth: 120,
      },
      {
        header: t('카메라 연결대수'),
        name: 'cameraCount',
        align: 'center',
        sortable: true,
        minWidth: 120,
        renderer: { classNames: ['text_secondary'] },
      },
    ]);
  }, [userInfo.prejobtypeYn, i18n.language]);

  useEffect(() => {
    setConstruction({ type: subCodeFilter('N'), [subCodeFilter('N')]: '', cdName: '' });
    setCamCcd({ type: subCodeFilter('O'), [subCodeFilter('O')]: '', cdName: '' });

    setPmsNormalSubList('N', setConstructionList);
    setPmsNormalSubList('O', setCamCList, setOrgCamCList);
  }, [userInfo.hCd]);

  useEffect(() => {
    getHeadNvrAPI().then((res) => {
      const { data, status } = res;
      if (status === 200) {
        const newList = data.data.nvrList.map((el: any) => ({ type: 'nvrCd', nvrCd: el.nCd, cdName: el.nName, infraCd: el.infraCd, camCCd: el.camCCd }));
        setNvrList([{ type: 'nvrCd', nvrCd: '', cdName: '전체' }, ...newList]);
      }
    });
  }, [userInfo.hCd]);

  useEffect(() => {
    getHeadNvrAPI().then((res) => {
      const { data, status } = res;
      if (status === 200) {
        setComCdListState(LIST_COUNT, setListCountComCdList, false);
      }
    });
  }, [userInfo.hCd]);

  useEffect(() => {
    if (isF9Pressed) {
      onClickSearch();
      setIsF9Pressed(false);
    }
  }, [isF9Pressed]);

  useEffect(() => {
    setCamCcd({ type: subCodeFilter('O'), [subCodeFilter('O')]: '', cdName: '' }); // 검색옵션에 건설업종을 변경 한 경우 관리회사명 전체 선택 처리
    if (construction.CONSTRUCTION_CD === '') {
      // 검색옵션에 건설업종을 전체로 선택 한 경우 관리회사명 optionList 초기화
      setCamCList(orgCamCList);
    } else {
      const newNvrList = nvrList.filter((nvr) => {
        return nvr.infraCd === construction.CONSTRUCTION_CD;
      });
      const newCamCList = orgCamCList.filter((camC) => {
        const findNvr = newNvrList.find((newNvr) => camC.CAM_C_CD !== '' && camC.CAM_C_CD === newNvr.camCCd);
        return findNvr;
      });
      setCamCList([{ type: 'CAM_C_CD', CAM_C_CD: '', cdName: '전체' }, ...newCamCList]);
    }
  }, [construction]);

  // 건설업종 & 관리회사 조회
  const setPmsNormalSubList = async (subCd: string, setState: Dispatch<SetStateAction<ISelectBox[]>>, setOrgState?: Dispatch<SetStateAction<ISelectBox[]>>) => {
    const req = { hCd: userInfo.hCd, sCd: userInfo.sCd, subCd };
    const res = await apiGet({ path: '/code/pmsNormalTitle', req });
    const { data, status } = res;

    if (status === 200) {
      const cdFind: string = PMS_NORMAL_SUB_CODE.find((el: any) => el.code.includes(subCd))?.type || '';
      const newList = data.data.pmsNormalTitleList.map((el: any) => ({ type: cdFind, [cdFind]: el.subCd, cdName: el.cdName }));
      setState([{ type: cdFind, [cdFind]: '', cdName: '전체' }, ...newList]);
      if (setOrgState) setOrgState([{ type: cdFind, [cdFind]: '', cdName: '전체' }, ...newList]);
    }
  };

  const onClickTab = (tab: string) => {
    setCurrentTabMrCd(tab);
  };

  // 검색 버튼 클릭
  const onClickSearch = () => {
    setLoading(true);

    navigate({
      pathname: location.pathname,
      search: '',
    });

    getHeadNvrAPI().then((res) => {
      const { data, status } = res;

      if (status === 200) {
        setLoading(false);
        const newList = data.data.nvrList.map((el: any) => ({ ...el, nh: `${el.mName || '이름없음'} / ${el.mTel || '입력없음'}` }));
        const serachFilter = newList.filter(
          (el: any) =>
            (construction[subCodeFilter('N')] !== '' ? construction[subCodeFilter('N')].includes(el.infraCd) : el) &&
            // (nvrCd.nvrCd !== '' ? nvrCd.nvrCd.includes(el.nCd) : el) &&
            (camCcd.CAM_C_CD !== '' ? el.camCCd === camCcd.CAM_C_CD : el)
        );
        const filteredArray = serachFilter.filter((item: any) => {
          return item.nName.toLowerCase()?.includes(nvrName.nvrName?.toLowerCase());
        });
        setTableState(filteredArray);
      }
    });
  };

  // NVR 리스트 조회 API
  const getHeadNvrAPI = async () => {
    const req = { hCd: userInfo.hCd };
    const res = await apiGet({ path: '/head/nvr', req });
    const { data, statusCode } = res.data;
    if (statusCode === 200) {
      setLoading(false);
      // setOrgTableState(data.attendList);
      const newList = data.nvrList.map((el: any) => ({ ...el, nh: `${el.mName || '이름없음'} / ${el.mTel || '입력없음'}` }));
      setTableState(newList);
    } else {
      setLoading(false);
      // toast.error(t(ERROR));
    }
    return res;
  };

  const initiateSearchOptions = () => {
    setConstruction({ type: subCodeFilter('N'), [subCodeFilter('N')]: '', cdName: '' });
    // setNvrCd({ type: 'nvrCd', nvrCd: '', cdName: '' });
    setCamCcd({ type: subCodeFilter('O'), [subCodeFilter('O')]: '', cdName: '' });
    setNvrName({ nvrName: '' });
  };

  // 초기화 버튼 클릭
  const onClickInitiateSearchOption = () => {
    initiateSearchOptions();
  };

  // 엑셀 버튼
  const renderExcelBtn = () => {
    if (auth.excelAuth) {
      return (
        <BtnGhost onClick={onClickExcelSave}>
          <span className='icon-ms-xlsx'>X</span> {t('엑셀')}
        </BtnGhost>
      );
      // return <BtnLightBlue onClick={onClickExcelSave}>엑셀 저장</BtnLightBlue>;
    }
    return null;
  };

  // 엑셀 저장 버튼 클릭
  const onClickExcelSave = () => {
    if (tableState.length <= 0) return;
    setExcelBtn(true);
    logPost({
      hCd: userInfo.hCd,
      sCd: userInfo.sCd,
      userId: userInfo.userId,
      menu: '일 출역현황',
      action: '엑셀 저장',
      etc: ``,
    });
  };

  return (
    <div className='contents'>
      <div className='content-container oneColumn'>
        <Tab tabList={newTabList || tabInfo.tabList} currentTabMrCd={currentTabMrCd} setCurrentTabMrCd={setCurrentTabMrCd} onClickTab={onClickTab} size={size} />
        <Root loading={loading}>
          <SearchOptions>
            {isBottom && (
              <FloatingButtonToTop>
                {/* eslint-disable-next-line jsx-a11y/control-has-associated-label */}
                <button type='button' onClick={() => scrollToNodeTop(tableRef)}>
                  <IoChevronUpSharp size={20} style={{ stroke: 'white' }} />
                </button>
              </FloatingButtonToTop>
            )}
            <div className='inputsWrapper'>
              <div className='inputForm-row withLabel'>
                <label htmlFor='sjCd'>{t('건설업종')}</label>
                <div className='inputForm-col'>
                  <SearchSelectBoxSm
                    options={constructionList}
                    defaultOption={t('전체')}
                    state={construction}
                    setState={setConstruction}
                    stateKey={subCodeFilter('N')}
                    codeKey='cdName'
                    initiateKey={construction[subCodeFilter('N')]}
                    filterbar='filter-1-left'
                    optionHeight='height-md'
                  />
                </div>
              </div>

              <div className='inputForm-row withLabel'>
                <label htmlFor='sjCd'>{t('관리회사명')}</label>
                <div className='inputForm-col'>
                  <SearchSelectBoxSm
                    options={camCList}
                    defaultOption={t('전체')}
                    state={camCcd}
                    setState={setCamCcd}
                    stateKey={subCodeFilter('O')}
                    codeKey='cdName'
                    initiateKey={camCcd[subCodeFilter('O')]}
                    filterbar='filter-1-left'
                    optionHeight='height-md'
                  />
                </div>
              </div>
              {/* <div className='inputForm-row withLabel'>
                <label htmlFor='sjCd'>{t('NVR명')}</label>
                <div className='inputForm-col'>
                  <SearchSelectBoxSm
                    options={nvrList}
                    defaultOption={t('전체')}
                    state={nvrCd}
                    setState={setNvrCd}
                    stateKey='nvrCd'
                    codeKey='cdName'
                    initiateKey={nvrCd.nvrCd}
                    filterbar='filter-1-left'
                    optionHeight='height-md'
                  />
                </div>
              </div> */}
              <div className='inputForm-row'>
                <div className='inputForm-col'>
                  <Input label='' placeholder={t('NVR명')} type='text' id='nvrName' name='nvrName' state={nvrName} setState={setNvrName} />
                </div>
              </div>
            </div>

            <div className='inputsWrapper'>
              <div className='secondSearchOption'>
                <div className='flex-basic textBtnGroup'>
                  <ShortcutButton icon='search' buttonText='검색' shortcut='F9' onClick={onClickSearch} />
                  <BtnGhost onClick={onClickInitiateSearchOption}>{t('초기화')}</BtnGhost>
                  <div className='searchResult'>
                    {t('총')}
                    <span>{tableState.length}</span>
                    {t('개')}
                  </div>
                  <div className='inputForm-row'>
                    <div className='inputForm-col withLabelComCf'>
                      <label htmlFor='useYn'>{t('보기 설정')}</label>
                      <SelectBox
                        options={listCountComCdList}
                        defaultOption={listCount[LIST_COUNT]}
                        state={listCount}
                        setState={setListCount}
                        stateKey={LIST_COUNT}
                        initiateKey={listCount[LIST_COUNT]}
                        setTableLinesAPI={patchUserMenuAPI}
                        optionHeight='height-sm'
                        rsearch
                      />
                    </div>
                  </div>
                </div>
                <div className='flex-basic iconBtnGroup'>{renderExcelBtn()}</div>
              </div>
            </div>
          </SearchOptions>
          <TuiGridWrapper componentRef={componentRef}>
            <TuiGrid
              data={tableState}
              filterTableState={filterTableState}
              setFilterTableState={setFilterTableState}
              columns={columns}
              perPage={Number(listCount[LIST_COUNT])}
              excelBtn={excelBtn}
              setExcelBtn={setExcelBtn}
              usePagenation
              scrollX
              height={tuiHeight}
              eListYn={eListYn}
              eExcelYn={eExcelYn}
              frozenCount={frozenCount}
            />
          </TuiGridWrapper>
        </Root>
      </div>
      {loading && (
        <LoadingModalBackground>
          <PulseLoader className='flex-center' color='rgb(0, 122, 255)' size='1rem' />
        </LoadingModalBackground>
      )}
    </div>
  );
};

export default Hcctv2;
